import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit'
import { apiKey } from "./entities";
import _ from "lodash";
import { normalize } from "normalizr";
import { entityTypes } from './entities';
import { createHmac } from 'crypto';
import { get, isEmpty } from 'lodash';
import { createApolloFetch } from "apollo-fetch";

const { 
    REACT_APP_API_URL, 
    REACT_APP_API_URL_LOCAL, 
    REACT_APP_APIV2_URL,
} = process.env;
const apiUrl = REACT_APP_API_URL && REACT_APP_API_URL.startsWith('http') ? REACT_APP_API_URL : REACT_APP_API_URL_LOCAL || '';
export const api2Url = REACT_APP_APIV2_URL;

const payorFields = [
    "id",
    "type",
    "status",
    "contact_notes",
    "cognito_sub",
    "billing_firstname",
    "billing_lastname",
    "billing_phone",
    "billing_work_phone",
    "billing_email",
    "billing_street",
    "billing_city",
    "billing_state",
    "billing_zip",
    "info_name",
    "info_attn",
    "info_street",
    "info_city",
    "info_state",
    "info_zip"
];
const userFields = [
    'id',
    'email',
    'first_name',
    'last_name',
    'street',
    'city',
    'state',
    'zip',
    'phone',
    'gpi_cards',
    'gpi_bank_accounts'
];

export const callMyProfile = createAsyncThunk(`${apiKey}/callMyProfile`, async ({ id, token }, context) => {
    const graphQL = createApolloFetch({ uri: `${apiUrl}/graphql` });
    graphQL.use(({ options }, next) => {
        options.headers = options.headers || {};
        if (id && token) {
            options.headers['Authorization'] = `Bearer ${token}`;
        }
        next();
    });
    const fields = `${userFields.join(' ')} payor { ${payorFields.join(' ')} }`
    try {
        const { errors, data } = await graphQL({ query: `query { myProfile { ${fields} } }` })
        const { myProfile } = data;
        if (errors || isEmpty(myProfile)) return context.rejectWithValue(errors || { message: 'Session expired. Please login again.' });
        else {
            const user = myProfile;
            const authPayorData = {
                billing_email: user.email,
                billing_firstname: user.first_name,
                billing_lastname: user.last_name,
                billing_phone: user.phone,
                billing_street: user.street,
                billing_state: user.state,
                billing_city: user.city,
                billing_zip: user.zip
            };

            const payorData = user.payor ? { ...authPayorData, ...user.payor } : authPayorData;
            return { ...myProfile, payor: payorData, token };
        }
    } catch (e) {
        return context.rejectWithValue(e);
    }

})
export class GraphQLError extends Error {
    constructor(query, variables, errors) {
        super([
            `GraphQLError ${query}`,
            `variables ${JSON.stringify(variables, null, '    ')}`,
            `errors {\n${errors.map(error => `- ${error.message}`).join('\n')}}`
        ].join('\n'));
        this.query = query;
        this.variables = variables;
        this.errors = errors;
    }
}

export const callGraphQL = createAsyncThunk(`${apiKey}/callGraphQL`, async ({ query, variables, response, isPublic }, context) => {
    const graphQL = createApolloFetch({ uri: !!isPublic ? `${apiUrl}/graphql_public` : `${apiUrl}/graphql` });
    const authInfo = getAuthInfo(context.getState());
    if (!isPublic) {
        graphQL.use(({ options }, next) => {
            options.headers = options.headers || {};
            if (authInfo.token) {
                options.headers['Authorization'] = `Bearer ${authInfo.token}`;
            }
            next();
        });
    }
    const { errors, data } = await graphQL({ query, variables })
    if (errors) return context.rejectWithValue(errors);
    else return _.reduce(data, (items, value, key) => {
        if (!_.has(response, key)) return items;
        let schema = null;
        if (_.isString(response[key])) {
            schema = _.get(entityTypes, response[key], null)
        } else if (_.isArray(response[key])) {
            schema = response[key].map(s => _.get(entityTypes, s, null)).filter(s => s !== null);
        } else {
            items[key] = value;
        }
        return _.isEmpty(schema) ? items : _.merge(items, normalize(value, schema).entities);
    }, {});
});

export const callGraphQLv2 = createAsyncThunk(`${apiKey}/callGraphQLv2`, async ({ query, variables, isPublic }, context) => {
    const graphQL = createApolloFetch({ uri: `${api2Url}/${isPublic ? 'graphqlPublic' : 'graphql'}` });
    const authInfo = getAuthInfo(context.getState());
    graphQL.use(({ options }, next) => {
        options.headers = options.headers || {};
        if (authInfo?.token) {
            options.headers['Authorization'] = `Bearer ${authInfo.token}`;
        }
        next();
    });

    const { errors, data } = await graphQL({ query, variables })
    if (errors) return context.rejectWithValue(errors);
    else return data;
});

const slice = createSlice({
    name: apiKey,
    initialState: {
        authInfo: {},
        authError: null,
        status: {},
        loading: false,
        errors: null,
    },
    extraReducers: {
        [callGraphQL.pending]: (state, action) => {
            const sha256 = createHmac('sha256', 'a secret');
            sha256.update(JSON.stringify(action.meta));
            const hash = sha256.digest('hex');
            state.status[hash] = { loading: true, errors: null }
            return state;
        },
        [callGraphQL.rejected]: (state, action) => {
            const sha256 = createHmac('sha256', 'a secret');
            sha256.update(JSON.stringify(action.meta));
            const hash = sha256.digest('hex');
            state.status[hash] = { loading: false, errors: action.payload }
            return state;
        },
        [callGraphQL.fulfilled]: (state, action) => {
            const sha256 = createHmac('sha256', 'a secret');
            sha256.update(JSON.stringify(action.meta));
            const hash = sha256.digest('hex');
            state.status[hash] = { loading: false, errors: null }
            return state;
        },
        [callMyProfile.rejected]: (state, { error, payload }) => {
            state.authError = [payload || error];
            state.authInfo = {};
        },
        [callMyProfile.fulfilled]: (state, { payload }) => {
            state.authInfo = { ...state.authInfo, ...payload };
        }
    }
})
export const reducer = slice.reducer;
export default reducer;
export const selectApi = state => state[apiKey];
export const getApiErrors = createSelector(selectApi, api => api.errors);
export const isApiLoading = createSelector(selectApi, api => api.loading);
export const getAuthInfo = createSelector(selectApi, api => api.authInfo);
export const getAuthError = createSelector(selectApi, api => api.authError);
